import React, {
  FC,
  HTMLAttributes,
  useState,
  useEffect,
  useCallback,
} from "react";
import { Box, Heading, Text, Flex } from "@chakra-ui/core";
import { ImageBox } from "@engyalo/storefront-components";
import { Price } from "./Price";
import { Customization } from "./Customization";
import {
  IProductPricedPerCollectionLevel1 as productLevel1Type,
  IProductPricedPerCollectionLevel2 as productLevel2Type,
} from "@engyalo/storefront-types/lib/graphql/product";
import { ICustomizationDetail } from "./types";
import {
  IConfig,
  ICartDictionary,
  IProduct,
  IProductLevel2,
  IProductLevel3,
} from "@engyalo/storefront-types/lib/plugins/model";
import { ProductType } from "@engyalo/storefront-types/lib/graphql/global-types";

interface IProps extends HTMLAttributes<HTMLDivElement> {
  product: productLevel1Type;
  isLoading: boolean;
  cart: ICartDictionary;
  config: IConfig;
  enablePrice?: boolean | null;
  setCanAddToCart: (canAddToCart: boolean) => void;
  tFunction: (translation: string) => string;
  setproductToAdd: (product: IProduct) => void;
}

export const CustomProduct: FC<IProps> = ({
  product,
  isLoading,
  cart,
  config,
  tFunction,
  setCanAddToCart,
  enablePrice,
  setproductToAdd,
}: IProps) => {
  const [productChoosen, setProductChoosen] = useState({} as IProduct);
  const [productReady, setProductReady] = useState(false);
  const [customization, setCustomization] = useState(
    {} as ICustomizationDetail
  );
  const [requiredOptions, setRequiredOptions] = useState(0);
  const [requiredOptionsSelected, setRequiredOptionsSelected] = useState(0);

  const getProductFirstImage = (product: productLevel1Type): string => {
    if (product && product.imageURL) return product.imageURL[0] || "";
    return "";
  };

  useEffect((): void => {
    if (productReady) {
      setCanAddToCart(productReady);
      setproductToAdd(productChoosen);
    } else {
      setCanAddToCart(productReady);
    }
  }, [productReady, productChoosen]);

  useEffect((): void => {
    setProductChoosen({
      ...product,
      customizations: Object.keys(customization).map(
        (key) => customization[key]
      ),
    } as IProduct);
  }, [customization, product]);

  useEffect((): void => {
    if (product) {
      const { customizations } = product;

      if (customizations) {
        const requiredOptionsFilter = customizations.filter(
          (customizations: productLevel2Type | null) => {
            if (customizations) {
              const { size } = customizations;
              if (size && size > 0) {
                return true;
              }
            }
            return false;
          }
        );

        setRequiredOptions(requiredOptionsFilter.length);
      }
    }

    if (productChoosen) {
      const { customizations } = productChoosen;

      if (customizations) {
        const requiredOptionsSelectedFilter = customizations.filter(
          (customization: IProductLevel2 | null) => {
            if (customization) {
              const {
                size,
                quantity,
                type,
                customizations: level3,
              } = customization;

              switch (type) {
                case ProductType.COUNTER:
                  if (level3) {
                    let level3OptionsSelected = 0;
                    level3.forEach((data: IProductLevel3 | null) => {
                      if (data) {
                        const { quantity } = data;
                        if (quantity) {
                          level3OptionsSelected += quantity;
                        }
                      }
                    });

                    return size === level3OptionsSelected;
                  }
                  return false;

                default:
                  if (size !== 0 && size === quantity) {
                    return true;
                  }
                  return false;
              }
            }

            return false;
          }
        );

        setRequiredOptionsSelected(requiredOptionsSelectedFilter.length);
      }
    }
  }, [productChoosen]);

  useEffect(() => {
    if (requiredOptions === requiredOptionsSelected) {
      setProductReady(true);
    } else {
      setProductReady(false);
    }
  }, [requiredOptions, requiredOptionsSelected, setProductReady]);

  const saveCustomization = useCallback(
    (data: IProductLevel2): void => {
      const { id: idToSave } = data;
      if (idToSave) {
        const savedCustomization = customization[idToSave];
        if (savedCustomization) {
          const savedJSON = JSON.stringify(savedCustomization);
          const newJSON = JSON.stringify(data);

          if (savedJSON !== newJSON) {
            customization[idToSave] = data;
            setCustomization({ ...customization });
          }
        } else {
          customization[idToSave] = data;
          setCustomization({ ...customization });
        }
      }
    },
    [customization]
  );

  if (product) {
    const { type, customizations } = product;

    return (
      <>
        {type === ProductType.CUSTOM ? (
          <Box flex="1" overflow="auto">
            <Box paddingX="16px" marginBottom="24px">
              <ImageBox
                imageUrl={getProductFirstImage(product)}
                quantity={
                  cart[product.sku || ""] ? cart[product.sku || ""].quantity : 0
                }
              />
              <Heading marginY="4px" as="h2" fontSize="xl" flex="1">
                {product.name}
              </Heading>
              <Text
                fontSize="md"
                fontFamily="body"
                color="gray.100"
                paddingBottom="4px"
              >
                {product.description}
              </Text>
              {(!enablePrice || enablePrice === true) && (
                <Price
                  price={product.price}
                  currency={config.currencyPrefix}
                  fontSize="xl"
                />
              )}
            </Box>
            {/* Customizations */}
            <Box>
              {customizations && (
                <>
                  {customizations.map((level2: productLevel2Type | null) => {
                    if (level2 && level2.id) {
                      return (
                        <Customization
                          key={level2.id}
                          customization={level2}
                          isLoading={false}
                          config={config}
                          tFunction={tFunction}
                          saveCustomization={saveCustomization}
                        />
                      );
                    }
                  })}
                </>
              )}
            </Box>
            {/* Customizations */}
          </Box>
        ) : (
          <>
            <Box paddingX="16px">
              <ImageBox
                imageUrl={getProductFirstImage(product)}
                quantity={
                  cart[product.sku || ""] ? cart[product.sku || ""].quantity : 0
                }
              />
              <Heading marginY="4px" as="h3" fontSize="xl" flex="1">
                {product.name}
              </Heading>
              <Text
                fontSize="md"
                fontFamily="body"
                color="gray.100"
                paddingBottom="4px"
              >
                {product.description}
              </Text>
              <Price price={product.price} currency="Q." />
            </Box>
            <Flex
              flex="1"
              flexDirection="column"
              overflow="auto"
              marginTop="8px"
              paddingX="4px"
            ></Flex>
          </>
        )}
      </>
    );
  }
  return null;
};
